import { useState, useEffect } from "react";
import { getAuth, verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useToast } from "../../util/ToastProvider";

export default function SetupPassword() {
  const auth = getAuth();

  const [oobCode, setOobCode] = useState("");
  const [validCode, setValidCode] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("")
  const toast = useToast();

  // Extract oobCode from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("oobCode");
    const emailParam = params.get("email")
    if (code) {
      console.log(code)
      setOobCode(code);
      verifyPasswordResetCode(auth, code)
        .then(() => setValidCode(true))
        .catch(() => setError("Invalid or expired link"))
        .finally(() => setLoading(false));
    } else {
      setError("Invalid link");
      setLoading(false);
    }

    if (!emailParam) {
      setError("Invalid link: missing email");
      setLoading(false);
      return;
    }
  
    if (!isValidEmail(emailParam)) {
      setError("Invalid email format");
      setLoading(false);
      return;
    }

    setEmail(emailParam);
  }, []);

  const validatePassword = (pwd: string) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-={}\[\]|;:'",.<>/?]).{8,}$/;
    return regex.test(pwd);
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError("");
  
    if (!validatePassword(password)) {
      setError(
        "Password must contain at least 8 characters, including one uppercase letter, one number, and one special character."
      );
      return;
    }
  
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
  
    try {
      // 1️⃣ Reset the password using Firebase oobCode
      confirmPasswordReset(auth, oobCode, password).then(async t =>{
          console.log("value---->", t)
         // 2️⃣ Tell backend password is set → mark emailVerified true
          const functions = getFunctions();
          const manageProfileHandler = httpsCallable(functions, "manageProfile");
      
          const resp: any = await manageProfileHandler({
            action: "markEmailVerifiedAfterPasswordSetup",
            data: { email }, // send email from your component state
          });
      
          if (resp.data.success) {
            setSuccess(true);
            toast.success(resp.data.message || "Password set successfully.");
          } else {
            toast.error("Something went wrong while verifying your email.");
          }
          
      });
  
     
  
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong.");
    }
  };
  

  if (loading) return <div>Loading…</div>;

  if (success)
    return (
      <div className="max-w-md mx-auto mt-10 p-6 border rounded-lg bg-green-50">
        <h2 className="text-xl font-bold text-green-700 mb-4">Password Set!</h2>
  
        <p className="text-green-800">
          Your password has been updated. You can now{" "}
          <a
            href="/auth/login"
            className="text-blue-600 font-medium underline hover:text-blue-800"
          >
            sign in
          </a>.
        </p>
      </div>
    )

  if (!validCode)
    return (
      <div className="max-w-md mx-auto mt-10 p-6 border rounded-lg bg-rose-50">
        <h2 className="text-xl font-bold text-rose-700 mb-4">Invalid Link</h2>
        <p>The password setup link is invalid or expired.</p>
      </div>
    );

  return (
    <div className="max-w-md mx-auto   text-dark-text shadow">
      <h2 className="text-xl font-bold mb-4">Set Your Password</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-sm text-white">New Password</label>
          <input
            type="password"
            className="mt-2 block w-full rounded-xl border-0 ring-1 ring-inset ring-dark-border 
                      !bg-dark-background-secondary-medium px-3 py-2 !text-white !caret-white 
                      !outline-none !focus:outline-none focus:ring-2 !focus:ring-white"
          
            value={password}
            onChange={(e) => setPassword(e.target.value)}
           
          />
        </div>

        <div>
          <label className="text-sm text-white">Confirm Password</label>
          <input
            type="password"
            className="mt-2 block w-full rounded-xl border-0 ring-1 ring-inset ring-dark-border 
                      !bg-dark-background-secondary-medium px-3 py-2 !text-white !caret-white 
                      !outline-none !focus:outline-none focus:ring-2 !focus:ring-white"
          
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
        >
          Set Password
        </button>
      </form>
    </div>
  );
}
